import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import Reveal from "./Reveal";
import { GALLERY } from "../../data/content";

export default function Gallery() {
  const [index, setIndex] = useState(null);
  const open = index !== null;

  const close = useCallback(() => setIndex(null), []);
  const prev = useCallback(
    (e) => {
      e && e.stopPropagation();
      setIndex((i) => (i - 1 + GALLERY.length) % GALLERY.length);
    },
    []
  );
  const next = useCallback(
    (e) => {
      e && e.stopPropagation();
      setIndex((i) => (i + 1) % GALLERY.length);
    },
    []
  );

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, close, prev, next]);

  return (
    <section id="gallery" className="relative z-10 py-20 md:py-36 border-t border-white/[0.05]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Reveal className="mb-14 max-w-2xl">
          <div className="text-[0.7rem] uppercase tracking-[0.24em] text-amber mb-5">In Frame</div>
          <h2 className="font-display text-4xl md:text-5xl font-bold leading-[1.05] mb-5">
            Moments & <span className="text-amber">Appearances</span>
          </h2>
          <p className="text-ink-secondary leading-relaxed">
            On stage, in studio, and behind the scenes — a look at talks, interviews, and the work in motion.
          </p>
        </Reveal>

        {/* Masonry */}
        <div className="columns-2 md:columns-3 lg:columns-4 gap-4">
          {GALLERY.map((g, i) => (
            <button
              key={g.src}
              data-testid={`gallery-item-${i}`}
              onClick={() => setIndex(i)}
              className="group relative block w-full mb-4 overflow-hidden rounded-xl border border-white/[0.06] bg-surface focus:outline-none focus:ring-2 focus:ring-amber"
            >
              <img
                src={g.src}
                alt={`Amiran Azaladze ${i + 1}`}
                loading="lazy"
                className="w-full h-auto object-cover grayscale-[35%] group-hover:grayscale-0 scale-100 group-hover:scale-[1.04] transition-all duration-500 ease-out"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-obsidian/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <span className="pointer-events-none absolute bottom-3 left-3 text-[0.65rem] uppercase tracking-[0.16em] text-ink opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300">
                View
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              data-testid="gallery-lightbox"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={close}
              className="fixed inset-0 z-[200] flex items-center justify-center bg-obsidian/95 backdrop-blur-md p-4 sm:p-10"
            >
            <button
              data-testid="lightbox-close"
              onClick={close}
              className="absolute top-5 right-5 h-11 w-11 rounded-full border border-white/15 flex items-center justify-center text-ink hover:bg-amber hover:text-obsidian hover:border-amber transition-colors duration-300"
              aria-label="Close"
            >
              <X size={20} />
            </button>

            <button
              data-testid="lightbox-prev"
              onClick={prev}
              className="absolute left-3 sm:left-8 h-12 w-12 rounded-full border border-white/15 flex items-center justify-center text-ink hover:bg-amber hover:text-obsidian hover:border-amber transition-colors duration-300"
              aria-label="Previous"
            >
              <ChevronLeft size={22} />
            </button>
            <button
              data-testid="lightbox-next"
              onClick={next}
              className="absolute right-3 sm:right-8 h-12 w-12 rounded-full border border-white/15 flex items-center justify-center text-ink hover:bg-amber hover:text-obsidian hover:border-amber transition-colors duration-300"
              aria-label="Next"
            >
              <ChevronRight size={22} />
            </button>

            <AnimatePresence mode="wait">
              <motion.img
                key={GALLERY[index].src}
                src={GALLERY[index].src}
                alt={`Amiran Azaladze ${index + 1}`}
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.99 }}
                transition={{ duration: 0.25 }}
                onClick={(e) => e.stopPropagation()}
                className="max-h-[82vh] max-w-[90vw] object-contain rounded-lg shadow-2xl"
              />
            </AnimatePresence>

            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-sm text-ink-muted tabular-nums">
              {index + 1} / {GALLERY.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>,
        document.body
      )}
    </section>
  );
}
